import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * useNotificationAudio
 * --------------------
 * Hook that loads the user's audio manifest and exposes a `play(event)`
 * function. Silent when admin globally disabled, user muted, or no URL.
 *
 * Usage:
 *   const { play, manifest, reload } = useNotificationAudio(token);
 *   play('tier_changed');
 */
export default function useNotificationAudio(token) {
  const [manifest, setManifest] = useState(null);
  const audioElRef = useRef(null);

  // Lazy create a single hidden <audio> element on first call
  const getAudioEl = useCallback(() => {
    if (audioElRef.current) return audioElRef.current;
    if (typeof Audio === 'undefined') return null;
    const el = new Audio();
    el.preload = 'auto';
    audioElRef.current = el;
    return el;
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/member/preferences/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.ok) setManifest(res.data.manifest);
    } catch (e) {
      /* silent — audio is best-effort */
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const play = useCallback(
    (eventKey) => {
      if (!manifest || !manifest.enabled) return;
      const url = manifest.event_url_map?.[eventKey];
      if (!url) return;
      try {
        const el = getAudioEl();
        if (!el) return;
        el.volume = typeof manifest.volume === 'number' ? manifest.volume : 0.5;
        // Resolve relative `/api/static/...` URLs against the backend origin
        // so audio plays correctly even when the page itself isn't served
        // by the API host (preview vs production environments).
        const absUrl = url.startsWith('http')
          ? url
          : `${BACKEND_URL}${url.startsWith('/') ? '' : '/'}${url}`;
        // Some browsers cache; force reset to allow rapid replays
        el.src = absUrl;
        el.currentTime = 0;
        const p = el.play();
        if (p && typeof p.catch === 'function') {
          // Autoplay restrictions: silently ignore rejected promise
          p.catch(() => {});
        }
      } catch (_) {
        /* silent */
      }
    },
    [manifest, getAudioEl],
  );

  return { manifest, play, reload: load };
}
